'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { handleImageError } from '@/utils/image-error';
interface Props {
  alt: string;
  src: string;
  isLazy?: boolean;
  className?: string;
  loaderClassName?: string;
  width?: number;
  height?: number;
}
const LazyImage = ({
  isLazy = false,
  alt,
  src,
  className,
  loaderClassName,
  width,
  height
}: Props) => {
  const [isReached, setIsReached] = useState(!isLazy);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!isLazy) return;
    const imageObserver = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) return;
        if (!isLazy || !entry) return;
        const image = entry.target as HTMLImageElement;
        setIsReached(true);
        imageObserver.unobserve(image);
        observer.disconnect();
      }
    }, {
      rootMargin: '0px 0px 0px 0px'
    });
    if (!imageRef.current) return;
    imageObserver.observe(imageRef.current);

    // check if visible immediately
    const rect = imageRef.current.getBoundingClientRect();
    const {
      top,
      left,
      bottom,
      right
    } = rect;
    const {
      innerHeight,
      innerWidth
    } = window;
    const isInView = (top > 0 && top < innerHeight || bottom > 0 && bottom < innerHeight) && (left > 0 && left < innerWidth || right > 0 && right < innerWidth);
    isInView && setIsReached(true);
    return () => imageObserver.disconnect();
  }, [isLazy]);
  return isReached ? <img alt={alt} src={src} className={cn(className, {
    'lazy-image': isLazy
  })} onError={handleImageError} width={width} height={height} data-sentry-component="LazyImage" data-sentry-source-file="lazy-image.tsx" /> : <span ref={imageRef} className={cn('inline-block animate-pulse bg-gray-100', className, loaderClassName)} data-sentry-component="LazyImage" data-sentry-source-file="lazy-image.tsx" />;
};
export default LazyImage;